
import { v4 as uuidv4 } from "uuid";
import { 
  FeatureUserRating,
  FloatingFeature,
  FeatureBase,
} from "@models";
import { 
  UpgradableNumber,
  TemplateBase,
  Attributes,
  ActionLimitation,
  CreatureLimitation,
  HitDiceUpgradable,
  SENSES,
  SKILLS,
  Creature,
} from "@dndModels";
import { CreatureType, DamageType, SizeType } from "@dndModels/Types";

import CommonUtilities from "@utilities/common_utilities";
import { DAMAGE_TYPES, TRUE_DAMAGE_TYPES } from "@dndModels/Constants";
// import DandDCharacterUtilities from "@dndUtilities/char_utilities";

const common_util = CommonUtilities.getInstance();
// const char_util = DandDCharacterUtilities.getInstance();

/**
 * The new version of Transforms work like the new version of Transforms, 
 * but with the idea that it's about modifying a target (usually the character)
 * instead of just creating a new creature. 
 * 
 * We actually do make it into a TrueCreatureInstance, 
 * but it's marked as a Transform, and is stored in a different spot.
 * 
 * Things that are different from Transform:
 *  - Some aspects of the target are kept. Others are replaced, and others are transformed. 
 *      The easiest examples of this are when a transformation changes the ability scores. 
 *      Some of them replace one ability score with what was another before the transform, 
 *      like with the Apothecary subclass based on Jekyll/Hyde.
 *      Others replace some ability scores, but not others, 
 *      like with Wild Shape replacing the physical stats but keeping the mental ones the same.
 *  - Also many transforms take a selected Creature Stat Block into account when creating the transform.
 *      This means that there are possibly 3 things to draw on when creating the new Creature Instance:
 *      1. The Target (usually a Character, but possibly a Creature Instance)
 *      2. Possibly a Creature Stat Block
 *      3. Things from the Transform itself.
 *      These 3 get mixed together, following the design specified in the Transform, but drawing on aspects of the other 1 or 2, 
 *      to create the resulting Creature Instance.
 * 
 * To get this to work we need to make it so for things where we can have a single UGN for Transform, we need to have 2 UGNs.
 * These paired UGNs will have one use the Target, and the other use the Creature Stat Block. 
 * Every time we do this, the resulting number will be the sum of the two results.
 * In most cases, one or the other will be blank, but there will likely be some cases where both are not blank.
 */
export class TransformOption {
  true_id = "";
  name = "";
  description = "";
  connected = false;
  image_url = "";

  get _id() { return this.true_id; }

  use_creature = false; // If false then it's just a Transform that takes into account the target. If true it also takes into account a chosen Creature Stat Block.
  creature_limit = new CreatureLimitation(); // This limit determines what kinds of Creatures can be used if use_creature is true.
  
  creature_type: CreatureType | "Inherit from Target" | "Inherit from Creature" = "Inherit from Target"; // Beast, Dragon, Humanoid, etc.
  tags: string[] = [];
  swarm: "Yes" | "No" | "Inherit from Target" | "Inherit from Creature" = "Inherit from Target";
  
  pb_from_char = true;
  pb_from_creature = false;
  extra_pb = new UpgradableNumber();

  cr_from_char = true;
  cr_from_creature = false;
  extra_cr = new UpgradableNumber();
  
  size: SizeType | "From Char" | "From Creature" = "From Char";

  max_hp_from_char = true;
  max_hp_from_creature = false;
  extra_max_hp = new UpgradableNumber();
  temp_hit_points = new PairedUGN();

  init_from_char = true;
  init_from_creature = false;
  extra_init = new UpgradableNumber();
  ac_from_char = true;
  ac_from_creature = false;
  extra_ac = new UpgradableNumber();
  
  ability_scores = new TransformStats();

  // These limits determine which kinds of actions get inherited from the target and creature.
  char_action_limits = new ActionLimitation();
  creature_action_limits: ("Traits" | "Actions" | "Bonus Actions" | "Reactions" | "Legendary Actions" | "Actions" | "Spells")[] = [];
  // These are extra features added in as part of the Transform.
  fbs: FeatureBase[] = [];

  // We can add in Creature Gifts as part of a Transform, just like we can with a Transform.
  built_in_gift_ids: string[] = [];

  // attributes
  attributes = new TransformAttributes();

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.description = obj.description ? obj.description : "";
      this.image_url = obj.image_url ? obj.image_url : "";

      this.use_creature = obj.use_creature ? obj.use_creature : false;
      this.creature_limit = obj.creature_limit ? new CreatureLimitation(obj.creature_limit) : new CreatureLimitation();
      
      this.creature_type = obj.creature_type ? obj.creature_type : "Inherit from Target";
      this.tags = obj.tags ? obj.tags : [];
      this.swarm = obj.swarm ? obj.swarm : "Inherit from Target";
      
      this.pb_from_char = obj.pb_from_char !== undefined ? obj.pb_from_char : true;
      this.pb_from_creature = obj.pb_from_creature !== undefined ? obj.pb_from_creature : false;
      this.extra_pb = obj.extra_pb ? new UpgradableNumber(obj.extra_pb) : new UpgradableNumber();
      
      this.cr_from_char = obj.cr_from_char !== undefined ? obj.cr_from_char : true;
      this.cr_from_creature = obj.cr_from_creature !== undefined ? obj.cr_from_creature : false;
      this.extra_cr = obj.extra_cr ? new UpgradableNumber(obj.extra_cr) : new UpgradableNumber();
      
      this.size = obj.size ? obj.size : "From Char";

      this.max_hp_from_char = obj.max_hp_from_char !== undefined ? obj.max_hp_from_char : true;
      this.max_hp_from_creature = obj.max_hp_from_creature ? obj.max_hp_from_creature : false;
      this.extra_max_hp = obj.extra_max_hp ? new UpgradableNumber(obj.extra_max_hp) : new UpgradableNumber();
      this.temp_hit_points = obj.temp_hit_points ? new PairedUGN(obj.temp_hit_points) : new PairedUGN();
    
      this.init_from_char = obj.init_from_char !== undefined ? obj.init_from_char : true;
      this.init_from_creature = obj.init_from_creature ? obj.init_from_creature : false;
      this.extra_init = obj.extra_init ? new UpgradableNumber(obj.extra_init) : new UpgradableNumber();
      this.ac_from_char = obj.ac_from_char !== undefined ? obj.ac_from_char : true;
      this.ac_from_creature = obj.ac_from_creature ? obj.ac_from_creature : false;
      this.extra_ac = obj.extra_ac ? new UpgradableNumber(obj.extra_ac) : new UpgradableNumber();
      
      this.ability_scores = obj.ability_scores ? new TransformStats(obj.ability_scores) : new TransformStats();
    
      this.char_action_limits = obj.char_action_limits ? new ActionLimitation(obj.char_action_limits) : new ActionLimitation();
      this.creature_action_limits = obj.creature_action_limits && obj.creature_action_limits.length > 0 ? obj.creature_action_limits : [];
      this.fbs = obj.fbs ? obj.fbs.map((o: any) => new FeatureBase(o)) : [];
    
      this.built_in_gift_ids = obj.built_in_gift_ids ? obj.built_in_gift_ids : [];
    
      this.attributes = obj.attributes ? new TransformAttributes(obj.attributes) : new TransformAttributes();
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      description: this.description,
      image_url: this.image_url,

      use_creature: this.use_creature,
      creature_limit: this.creature_limit.toDBObj(),

      creature_type: this.creature_type,
      tags: this.tags,
      swarm: this.swarm,
      
      pb_from_char: this.pb_from_char,
      pb_from_creature: this.pb_from_creature,
      extra_pb: this.extra_pb.toDBObj(),

      cr_from_char: this.cr_from_char,
      cr_from_creature: this.cr_from_creature,
      extra_cr: this.extra_cr.toDBObj(),
      
      size: this.size,

      max_hp_from_char: this.max_hp_from_char,
      max_hp_from_creature: this.max_hp_from_creature,
      extra_max_hp: this.extra_max_hp.toDBObj(),
      temp_hit_points: this.temp_hit_points.toDBObj(),

      init_from_char: this.init_from_char,
      init_from_creature: this.init_from_creature,
      extra_init: this.extra_init.toDBObj(),
      ac_from_char: this.ac_from_char,
      ac_from_creature: this.ac_from_creature,
      extra_ac: this.extra_ac.toDBObj(),
      
      ability_scores: this.ability_scores.toDBObj(),

      char_action_limits: this.char_action_limits.toDBObj(),
      creature_action_limits: this.creature_action_limits,
      fbs: this.fbs.map(o => o.toDBObj()),

      built_in_gift_ids: this.built_in_gift_ids,

      attributes: this.attributes.toDBObj(),
    };
  }

  copy(copyMe: TransformOption, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.image_url = copyMe.image_url;

    this.use_creature = copyMe.use_creature;
    this.creature_limit = copyMe.creature_limit.clone();
    
    this.creature_type = copyMe.creature_type;
    this.tags = copyMe.tags;
    this.swarm = copyMe.swarm;
    
    this.pb_from_char = copyMe.pb_from_char;
    this.pb_from_creature = copyMe.pb_from_creature;
    this.extra_pb = copyMe.extra_pb.clone();
    
    this.cr_from_char = copyMe.cr_from_char;
    this.cr_from_creature = copyMe.cr_from_creature;
    this.extra_cr = copyMe.extra_cr.clone();
    
    this.size = copyMe.size;

    this.max_hp_from_char = copyMe.max_hp_from_char;
    this.max_hp_from_creature = copyMe.max_hp_from_creature;
    this.extra_max_hp = copyMe.extra_max_hp.clone();
    this.temp_hit_points = copyMe.temp_hit_points.clone();
  
    this.init_from_char = copyMe.init_from_char;
    this.init_from_creature = copyMe.init_from_creature;
    this.extra_init = copyMe.extra_init.clone()
    this.ac_from_char = copyMe.ac_from_char;
    this.ac_from_creature = copyMe.ac_from_creature;
    this.extra_ac = copyMe.extra_ac.clone();
    
    this.ability_scores = copyMe.ability_scores.clone();
  
    this.char_action_limits = copyMe.char_action_limits.clone();
    this.creature_action_limits = [...copyMe.creature_action_limits];
    this.fbs = copyMe.fbs.map(o => o.clone());
  
    this.built_in_gift_ids = copyMe.built_in_gift_ids;
  
    this.attributes = copyMe.attributes.clone();
  }

  clone(drop_ids = false): TransformOption {
    const new_obj = new TransformOption();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copyTemplate(copyMe: TemplateBase, all_floating_features: FloatingFeature[]): void {
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.connected = false;
  }

  connectUserRatings(userRatings: FeatureUserRating[]) {
    // this.actions.forEach(feature => {
    //   feature.connectUserRatings(userRatings);
    // });
    // this.legendary_actions.forEach(feature => {
    //   feature.connectUserRatings(userRatings);
    // });
    // this.special_abilities.forEach(feature => {
    //   feature.connectUserRatings(userRatings);
    // });
  }
}

export class PairedUGN {
  from_target = new UpgradableNumber();
  from_creature = new UpgradableNumber();
  
  constructor(obj?: any, log_it = false) {
    if (obj) {
      this.from_target = obj.from_target ? new UpgradableNumber(obj.from_target) : new UpgradableNumber();
      this.from_creature = obj.from_creature ? new UpgradableNumber(obj.from_creature) : new UpgradableNumber();
    }
  }

  toDBObj(): any {
    return {
      from_target: this.from_target.toDBObj(),
      from_creature: this.from_creature.toDBObj(),
    };
  }

  copy(copyMe: PairedUGN): void {
    this.from_target = copyMe.from_target.clone();
    this.from_creature = copyMe.from_creature.clone();
  }

  clone(): PairedUGN {
    const new_obj = new PairedUGN();
    new_obj.copy(this);
    return new_obj;
  }
}

export class TransformStats {
  strength = new PairedUGN();
  dexterity = new PairedUGN();
  constitution = new PairedUGN();
  intelligence = new PairedUGN();
  wisdom = new PairedUGN();
  charisma = new PairedUGN();

  constructor(obj?: any) {
    if (obj) {
      this.strength = obj.strength ? new PairedUGN(obj.strength) : new PairedUGN();
      this.dexterity = obj.dexterity ? new PairedUGN(obj.dexterity) : new PairedUGN();
      this.constitution = obj.constitution ? new PairedUGN(obj.constitution) : new PairedUGN();
      this.intelligence = obj.intelligence ? new PairedUGN(obj.intelligence) : new PairedUGN();
      this.wisdom = obj.wisdom ? new PairedUGN(obj.wisdom) : new PairedUGN();
      this.charisma = obj.charisma ? new PairedUGN(obj.charisma) : new PairedUGN();
    }
  }

  toDBObj(): any {
    return {
      strength: this.strength.toDBObj(),
      dexterity: this.dexterity.toDBObj(),
      constitution: this.constitution.toDBObj(),
      intelligence: this.intelligence.toDBObj(),
      wisdom: this.wisdom.toDBObj(),
      charisma: this.charisma.toDBObj()
    };
  }

  copy(copyMe: TransformStats): void {
    this.strength = copyMe.strength.clone();
    this.dexterity = copyMe.dexterity.clone();
    this.constitution = copyMe.constitution.clone();
    this.intelligence = copyMe.intelligence.clone();
    this.wisdom = copyMe.wisdom.clone();
    this.charisma = copyMe.charisma.clone();
  }

  clone(): TransformStats {
    const new_obj = new TransformStats();
    new_obj.copy(this);
    return new_obj;
  }
}

/**
 * This is used to create the attributes of the resulting Creature Instance, 
 * using those from the Target, the Creature Stat Block (if present),
 * and anything we specify here.
 * 
 * attributes is set as a default.
 * from_target has things that can be inherited from the target.
 * from_creature has things that can be inherited from the Creature Stat Block (if present).
 * 
 * Any that are present in more than one, it takes whatever the best case is.
 */
export class TransformAttributes { 
  attributes = new Attributes(); 
  from_target = new InheritAttributes();
  from_creature = new InheritAttributes();
  
  constructor(obj?: any) {
    if (obj) {
      this.attributes = obj.attributes ? new Attributes(obj.attributes) : new Attributes();
      this.from_target = obj.from_target ? new InheritAttributes(obj.from_target) : new InheritAttributes();
      this.from_creature = obj.from_creature ? new InheritAttributes(obj.from_creature) : new InheritAttributes();
    }
  }

  toDBObj(): any {
    return {
      attributes: this.attributes.toDBObj(),
      from_target: this.from_target.toDBObj(),
      from_creature: this.from_creature.toDBObj(),
    };
  }

  copy(copyMe: TransformAttributes): void {
    this.attributes = copyMe.attributes.clone();
    this.from_target = copyMe.from_target.clone();
    this.from_creature = copyMe.from_creature.clone();
  }

  clone(): TransformAttributes {
    const new_obj = new TransformAttributes();
    new_obj.copy(this);
    return new_obj;
  }
}

/**
 * If it's present here then it means it can inherit that attribute from that thing.
 */
export class InheritAttributes { 
  damage_immunities: DamageType[] = []; 
  damage_resistances: DamageType[] = [];
  damage_vulnerabilities: DamageType[] = [];
  extra_damage_immunities = false;
  extra_damage_resistances = false;
  extra_damage_vulnerabilities = false;
  allDamageMultiplier(multiplier: number, set: boolean | undefined = undefined) {
    if (set === undefined) {
      if (multiplier === 0) {
        return this.extra_damage_immunities && common_util.listsMatch(this.damage_immunities, TRUE_DAMAGE_TYPES);
      } else if (multiplier === 0.5) {
        return this.extra_damage_resistances && common_util.listsMatch(this.damage_resistances, TRUE_DAMAGE_TYPES);
      } else if (multiplier === 2) {
        return this.extra_damage_vulnerabilities && common_util.listsMatch(this.damage_vulnerabilities, TRUE_DAMAGE_TYPES);
      }
    } else {
      if (multiplier === 0) {
        this.extra_damage_immunities = set;
        this.damage_immunities = set ? TRUE_DAMAGE_TYPES : [];
      } else if (multiplier === 0.5) {
        this.extra_damage_resistances = set;
        this.damage_resistances = set ? TRUE_DAMAGE_TYPES : [];
      } else if (multiplier === 2) {
        this.extra_damage_vulnerabilities = set;
        this.damage_vulnerabilities = set ? TRUE_DAMAGE_TYPES : [];
      }
      return true;
    } 
    return false;
  }
  condition_immunities: string[] = [];

  senses: string[] = [];
  skill_profs: string[] = [];
  save_profs: string[] = [];
  speeds: string[] = [];
  hover = false;
  tool_profs: string[] = [];
  languages: string[] = [];
  get all_speeds() {
    return this.hover && common_util.listsMatch(this.speeds, ["walk","swim","climb","fly","burrow"]);
  }
  get all_senses() {
    return common_util.listsMatch(this.senses, SENSES.map(o => o.name));
  }
  get all_skills() {
    return common_util.listsMatch(this.skill_profs, SKILLS.map(o => o._id));
  }
  
  constructor(obj?: any) {
    if (obj) {
      this.damage_immunities = obj.damage_immunities ? obj.damage_immunities : [];
      this.damage_resistances = obj.damage_resistances ? obj.damage_resistances : [];
      this.damage_vulnerabilities = obj.damage_vulnerabilities ? obj.damage_vulnerabilities : [];
      this.extra_damage_immunities = obj.extra_damage_immunities ? obj.extra_damage_immunities : false;
      this.extra_damage_resistances = obj.extra_damage_resistances ? obj.extra_damage_resistances : false;
      this.extra_damage_vulnerabilities = obj.extra_damage_vulnerabilities ? obj.extra_damage_vulnerabilities : false;
      this.condition_immunities = obj.condition_immunities ? obj.condition_immunities : [];
      this.senses = obj.senses ? obj.senses : [];
      this.skill_profs = obj.skill_profs ? obj.skill_profs : [];
      this.save_profs = obj.save_profs ? obj.save_profs : [];
      this.speeds = obj.speeds ? obj.speeds : [];
      this.hover = obj.hover ? obj.hover : false;
      this.tool_profs = obj.tool_profs ? obj.tool_profs : [];
      this.languages = obj.languages ? obj.languages : [];
    }
  }

  toDBObj(): any {
    return {
      damage_immunities: this.damage_immunities,
      damage_resistances: this.damage_resistances,
      damage_vulnerabilities: this.damage_vulnerabilities,
      extra_damage_immunities: this.extra_damage_immunities,
      extra_damage_resistances: this.extra_damage_resistances,
      extra_damage_vulnerabilities: this.extra_damage_vulnerabilities,
      condition_immunities: this.condition_immunities,
      senses: this.senses,
      skill_profs: this.skill_profs,
      save_profs: this.save_profs,
      speeds: this.speeds,
      hover: this.hover,
      tool_profs: this.tool_profs,
      languages: this.languages,
    };
  }

  copy(copyMe: InheritAttributes): void {
    this.damage_immunities = [...copyMe.damage_immunities];
    this.damage_resistances = [...copyMe.damage_resistances];
    this.damage_vulnerabilities = [...copyMe.damage_vulnerabilities];
    this.extra_damage_immunities = copyMe.extra_damage_immunities;
    this.extra_damage_resistances = copyMe.extra_damage_resistances;
    this.extra_damage_vulnerabilities = copyMe.extra_damage_vulnerabilities;
    this.condition_immunities = [...copyMe.condition_immunities];
    this.senses = [...copyMe.senses];
    this.skill_profs = [...copyMe.skill_profs];
    this.save_profs = [...copyMe.save_profs];
    this.speeds = [...copyMe.speeds];
    this.hover = copyMe.hover;
    this.tool_profs = [...copyMe.tool_profs];
    this.languages = [...copyMe.languages];
  }

  clone(): InheritAttributes {
    const new_obj = new InheritAttributes();
    new_obj.copy(this);
    return new_obj;
  }

  get speed_string() {
    return "";
  }
  get senses_string() {
    return "";
  }
  get immunities_string() {
    return "";
  }
  get resistances_string() {
    return "";
  }
  get vulnerabilities_string() {
    return "";
  }
  get condition_immunities_string() {
    return "";
  }
  get tools_string() {
    return "";
  }
  get skills_string() {
    return "";
  }
  get saving_throws_string() {
    return "";
  }
}

export class TransformOption2 {
  custom_stat_block?: TransformOption;
  creature?: Creature;
  count = 0;
  description = "";
  get tags() {
    const tags: string[] = [];
    if (this.custom_stat_block) {
      tags.push(...this.custom_stat_block.tags);
    }
    if (this.creature) {
      tags.push(...this.creature.tags);
    }
    return tags;
  }

  get challenge_rating() {
    if (this.creature) {
      return this.creature.challenge_rating;
    } 
    return 0;
  }

  get _id() {
    if (this.creature) {
      return this.creature._id;
    } else if (this.custom_stat_block) {
      return this.custom_stat_block.true_id;
    }
    return "";
  }

  get creature_type() {
    if (this.creature) {
      return this.creature.creature_type;
    } else if (this.custom_stat_block) {
      return this.custom_stat_block.creature_type;
    }
    return "None";
  }
  get age_category() {
    if (this.creature) {
      return this.creature.age_category;
    } 
    return "";
  }
  // get size_num() {
  //   if (this.creature) {
  //     return this.creature.size_num;
  //   } else if (this.custom_stat_block) {
  //     return this.custom_stat_block.size_num;
  //   }
  //   return -1;
  // }
  // get min_challenge_rating() {
  //   if (this.creature) {
  //     return this.creature.min_challenge_rating;
  //   } else if (this.custom_stat_block) {
  //     return this.custom_stat_block.challenge_rating.base;
  //   }
  //   return 0;
  // }
  // get max_challenge_rating() {
  //   if (this.creature) {
  //     return this.creature.max_challenge_rating;
  //   } else if (this.custom_stat_block) {
  //     return this.custom_stat_block.challenge_rating.base;
  //   }
  //   return 0;
  // }
  // get flying() {
  //   if (this.creature) {
  //     return this.creature.flying;
  //   } else if (this.custom_stat_block) {
  //     return this.custom_stat_block.flying;
  //   }
  //   return false;
  // }
  // get swimming() {
  //   if (this.creature) {
  //     return this.creature.swimming;
  //   } else if (this.custom_stat_block) {
  //     return this.custom_stat_block.swimming;
  //   }
  //   return false;
  // }

  // get subtypes() {
  //   if (this.creature) {
  //     return this.creature.subtypes;
  //   } else if (this.custom_stat_block) {
  //     return this.custom_stat_block.subtypes;
  //   }
  //   return [];
  // }

  get swarm() {
    if (this.creature) {
      return this.creature.swarm;
    } else if (this.custom_stat_block) {
      return this.custom_stat_block.swarm;
    }
    return false;
  }

  get name() {
    if (this.creature) {
      return this.creature.name;
    } else if (this.custom_stat_block) {
      return this.custom_stat_block.name;
    }
    return "";
  }
}
